import { inject, Injectable } from '@angular/core';
import { doc, docData, Firestore } from '@angular/fire/firestore';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { OrganizationService } from '../organization/organization.service';

/**
 * Listens to the materialized offline-device count for the active organization
 * and shares it between the side-menu badge and the Home page tile.
 */
@Injectable({
  providedIn: 'root'
})
export class DeviceStatusService {
  private firestore: Firestore = inject(Firestore);

  private offlineCountSubject = new BehaviorSubject<number>(0);
  /** Latest count of visible, offline devices in the active organization. */
  offlineCount$: Observable<number> = this.offlineCountSubject.asObservable();

  private orgChangeSubscription?: Subscription;
  private counterSubscription?: Subscription;
  private isStarted = false;

  constructor(
    private authService: AuthService,
    private organizationService: OrganizationService
  ) { }

  /** Starts the organization-scoped counter listener. Safe to call repeatedly. */
  start(): void {
    if (this.isStarted || !this.canReadDevices()) {
      return;
    }

    this.isStarted = true;

    if (this.authService.userHasRole('superadmin')) {
      this.orgChangeSubscription = this.organizationService.selectedOrganization$
        .subscribe(organization => this.listenToOrganization(organization));
    } else {
      this.listenToOrganization(this.authService.getCurrentUserOrganization().name);
    }
  }

  /** Stops organization tracking and the active counter listener. */
  stop(): void {
    this.isStarted = false;
    this.orgChangeSubscription?.unsubscribe();
    this.orgChangeSubscription = undefined;
    this.counterSubscription?.unsubscribe();
    this.counterSubscription = undefined;
    this.offlineCountSubject.next(0);
  }

  private listenToOrganization(organizationName: string): void {
    this.counterSubscription?.unsubscribe();
    this.counterSubscription = undefined;
    this.offlineCountSubject.next(0);

    if (!organizationName) {
      return;
    }

    const organizationDocument = doc(this.firestore, `organizations/${organizationName}`);
    this.counterSubscription = docData(organizationDocument).subscribe({
      next: data => {
        const count = data?.['offlineDevicesCount'];
        this.offlineCountSubject.next(typeof count === 'number' ? count : 0);
      },
      error: () => {
        this.offlineCountSubject.next(0);
      }
    });
  }

  /**
   * Superadmins can always inspect device status (scoped to the organization they
   * pick); other users need device read rights, which `getDeviceStatus` requires.
   * Public so consumers can gate device-status UI on it.
   */
  canReadDevices(): boolean {
    if (this.authService.userHasRole('superadmin')) {
      return true;
    }

    return this.authService.userHasRole('devices_r') || this.authService.userHasRole('devices_rw');
  }
}
